import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { live } from '../data/live';
import {
  useBuildUpload, useConfig, useConnections, useFrameActions, useFrames, useReleaseMutations, useReleases,
  useChargePoints,
} from '../lib/queries';
import { qrSvg } from '../lib/qr';
import type { AppRelease, OcppFrame } from '../data/types';
import {
  MESSAGE_TYPE, chargingCount, frameDirection, frameSummary, freeNow, maxKw,
  chargePointLabel, chargePointTone,
} from '../data/types';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { DataSource } from './System';
import { errorText, loadFailedHint } from '../lib/errors';

/* A time alone is ambiguous the moment the log stops being live — a frame from
   last Tuesday reads as one from this morning. The date appears only when it is
   not today, so a busy live view stays narrow. */
const stamp = (iso: string): string => {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString()
    : `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString()}`;
};

/** "Dhaka, Bangladesh", or an honest reason there is no answer. */
const place = (o: {
  country: string | null; district: string | null; city: string | null; notFound: boolean;
}): string => {
  if (o.notFound) return 'no location for this address';
  if (!o.country) return 'not looked up yet';
  return [o.city, o.district, o.country].filter(Boolean).join(', ');
};

const since = (iso: string | null): string => {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86_400)}d ago`;
};

/* ------------------------------------------------------------- dashboard -- */

export function Dashboard() {
  const { data: res, isLoading, error } = useChargePoints();
  if (isLoading) return <Loading />;
  if (error || !res) {
    return <Empty title="The CSMS is not answering." hint={loadFailedHint(error)} />;
  }
  const data = res.items;
  const connectors = data.reduce((n, s) => n + s.connectors.length, 0);
  const free = data.reduce((n, s) => n + freeNow(s), 0);
  const charging = data.reduce((n, s) => n + chargingCount(s), 0);
  const blocked = data.filter((s) => s.blocked).length;

  return (
    <>
      <div className="tiles">
        {/* Real counts only. Invented headline figures on a live console are
            the fastest way to make every other number on it untrustworthy. */}
        <Tile k="Charge points" v={data.length} d={`${data.filter((s) => s.status !== 'offline').length} online`} />
        <Tile k="Free now" v={free} d={`of ${connectors} connectors`} />
        <Tile k="In use" v={charging} d="charging right now" />
        <Tile k="Blocked" v={blocked} d={blocked ? 'taken off the map' : 'none'} />
      </div>
      <DataSource />
      <div className="card" style={{ marginTop: 14 }}>
        <div className="li-title">Live charge points</div>
        <div className="tbl" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Charge point</th><th className="num">Connectors</th>
                <th className="num">Free</th><th className="num">Peak</th><th>State</th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td className="num mono">{s.connectors.length}</td>
                  <td className="num mono">{freeNow(s)}</td>
                  {/* Null when no bay has reported its power — a dash, not
                      "null kW", and not 0 kW either. */}
                  <td className="num mono">{maxKw(s) != null ? `${maxKw(s)} kW` : '—'}</td>
                  <td>
                    <Pill tone={chargePointTone(s)}>{chargePointLabel(s)}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------- OCPP messages ---- */

/**
 * The frames the CSMS recorded, as it recorded them.
 *
 * The console never opens a socket to a charger — this reads CitrineOS's own
 * message log through the BFF. That is why it can show traffic from before the
 * page was opened, which a browser-side tap could not.
 */
export function Ocpp() {
  const [polling, setPolling] = useState(true);
  const [dir, setDir] = useState<'all' | 'in' | 'out'>('all');
  const [action, setAction] = useState('all');
  const [cp, setCp] = useState('all');
  const [open, setOpen] = useState<number | null>(null);

  const { data: frames, isLoading, error } = useFrames(
    { direction: dir, action, chargePointId: cp }, polling);
  const { data: actions } = useFrameActions();
  const { data: conns } = useConnections();

  if (isLoading) return <Loading />;
  if (error || !frames) {
    return <Empty title="The message log is not answering." hint="Nothing is simulated in its place." />;
  }

  const errors = frames.filter((f) => f.type === 4).length;
  const total = actions?.reduce((n, a) => n + a.n, 0) ?? frames.length;
  const online = conns?.items.filter((c) => c.isOnline).length ?? 0;
  const newest = frames[0]?.timestamp ?? null;

  return (
    <>
      <div className="tiles">
        <Tile k="Messages" v={total.toLocaleString()} d="recorded by the CSMS" />
        <Tile k="Errors" v={errors} d={errors ? 'CALLERROR frames shown' : 'none in this view'} />
        <Tile k="Connected" v={online} d={`of ${conns?.items.length ?? 0} charge points`} />
        <Tile k="Last frame" v={since(newest)} d={polling ? 'polling every 4s' : 'paused'} />
      </div>

      {conns && conns.items.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="row">
            <div className="li-title">Connections</div>
            {!conns.geoDb.present && (
              <span className="li-sub faint" title={conns.geoDb.path}>
                addresses shown; no GeoLite2 database installed
              </span>
            )}
          </div>
          <div className="tbl" style={{ marginTop: 8 }}>
            <table>
              <thead>
                <tr>
                  <th>Charge point</th><th>Protocol</th><th>Connects from</th>
                  <th>Location</th><th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {conns.items.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span
                        title={c.presence === 'stale'
                          ? 'the CSMS flags this online, but it has gone silent'
                          : c.presence}
                        style={{
                          color: c.presence === 'online' ? 'var(--ok)'
                            : c.presence === 'stale' ? 'var(--warn, #C97A00)' : 'var(--ink-3)',
                        }}
                      >●</span>{' '}
                      <span className="li-title">{c.chargePointId ?? `#${c.id}`}</span>
                      {(c.vendor || c.model) && (
                        <div className="li-sub">{[c.vendor, c.model].filter(Boolean).join(' · ')}</div>
                      )}
                    </td>
                    <td className="li-sub mono">{c.protocol ?? 'unknown'}</td>
                    <td className="mono">
                      {c.origin
                        ? c.origin.ip
                        : <span className="faint">not seen since logging began</span>}
                    </td>
                    <td className="li-sub">
                      {c.origin ? (place(c.origin) || <span className="faint">—</span>) : <span className="faint">—</span>}
                    </td>
                    <td className="li-sub mono">
                      {since(c.lastSeenAt)}
                      {c.presence === 'stale' && (
                        <div style={{ color: 'var(--bad)' }}>flagged online, silent</div>
                      )}
                      {c.presence === 'never' && <div className="faint">never spoke</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="li-sub" style={{ marginTop: 8 }}>
            The address comes from the TLS proxy, not the CSMS — CitrineOS has no
            column for one, and every charger reaches it through the proxy anyway.
            A charge point with no address here has not connected since the proxy
            started recording.
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="chips">
          {(['all', 'in', 'out'] as const).map((d) => (
            <Chip key={d} selected={dir === d} onClick={() => setDir(d)}
                  label={d === 'all' ? 'All' : d === 'in' ? 'Charger → CSMS' : 'CSMS → Charger'} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="field" style={{ width: 'auto' }} value={cp}
                  onChange={(e) => setCp(e.target.value)}>
            <option value="all">All charge points</option>
            {[...new Set((conns?.items ?? []).map((c) => c.chargePointId).filter(Boolean))].map((id) => (
              <option key={id} value={id!}>{id}</option>
            ))}
          </select>
          <select className="field" style={{ width: 'auto' }} value={action}
                  onChange={(e) => setAction(e.target.value)}>
            <option value="all">All actions</option>
            {(actions ?? []).map((a) => (
              <option key={a.action} value={a.action}>{a.action} ({a.n})</option>
            ))}
          </select>
          <button className={`btn sm${polling ? '' : ' ghost'}`} onClick={() => setPolling(!polling)}>
            {polling ? '● Live' : '⏸ Paused'}
          </button>
        </div>
      </div>

      {!frames.length ? <Empty title="No frames match these filters." /> : (
        <div className="tbl">
          <table>
            <thead>
              <tr><th>Time</th><th /><th>Type</th><th>Action</th><th>Summary</th><th>Charge point</th></tr>
            </thead>
            <tbody>
              {frames.map((f) => <Frame key={f.id} f={f} open={open === f.id}
                                        onToggle={() => setOpen(open === f.id ? null : f.id)} />)}
            </tbody>
          </table>
        </div>
      )}

      <div className="li-sub" style={{ marginTop: 9 }}>
        ↑ charger → CSMS · ↓ CSMS → charger · click a row for the raw OCPP array
        exactly as it crossed the socket. This is the CSMS's own message log, not
        a tap opened by the browser.
      </div>
    </>
  );
}

function Frame({ f, open, onToggle }: { f: OcppFrame; open: boolean; onToggle: () => void }) {
  const dir = frameDirection(f);
  const summary = frameSummary(f);
  return (
    <tr onClick={onToggle} style={{ cursor: 'pointer' }}>
      <td className="mono" style={{ whiteSpace: 'nowrap' }}>
        {stamp(f.timestamp)}
      </td>
      <td className="mono" style={{ color: dir === 'in' ? 'var(--ok)' : 'var(--ink-3)' }}>
        {dir === 'in' ? '↑' : '↓'}
      </td>
      <td>
        <span className="pill" style={f.type === 4 ? { color: 'var(--bad)' } : undefined}>
          {MESSAGE_TYPE[f.type] ?? f.type}
        </span>
      </td>
      <td className="li-title">{f.action ?? '—'}</td>
      <td className="li-sub">
        {summary || <span className="faint">—</span>}
        {open && (
          <pre className="mono" style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', fontSize: 11 }}>
            {f.raw ?? JSON.stringify([f.type, f.uniqueId, f.action, f.payload])}
          </pre>
        )}
      </td>
      <td className="li-sub">{f.chargePointId}</td>
    </tr>
  );
}

/* ---------------------------------------------------------- app releases -- */

/**
 * The install code.
 *
 * A tester points a phone at the screen and gets the APK — no link to type, no
 * file to email, and no chance of installing yesterday's build because the
 * message was never updated. It encodes whatever `app.android_url` currently
 * points at, which is the same URL the app's own update prompt uses, so the two
 * can never disagree.
 *
 * Rendered white-on-black regardless of theme: a scanner needs the contrast and
 * an inverted code does not read.
 */
/**
 * "Check now" for every app that is currently open.
 *
 * Arming a forced update changes what the server answers; it does not reach a
 * phone until that phone asks. An app asks at launch and when it returns to the
 * foreground, which on Android can be days away — and those are the days the
 * build you are trying to stop keeps running. This closes that gap.
 *
 * It reaches open apps only, and deliberately does not pretend otherwise: the
 * count says how many are listening before you press it. Everyone else gets the
 * same answer at their next launch, because arming the update changed the
 * stored policy too.
 */
function PushButton({ platform }: { platform: string }) {
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const connected = useQuery({
    queryKey: ['apps-connected'],
    queryFn: () => live.appsConnected(),
    refetchInterval: 10_000,
  });
  const online = connected.data?.connected ?? 0;

  const push = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const r = await live.pushUpdate(platform);
      setSaid(r.sent === 0
        ? 'No apps were open. Everyone gets it at their next launch.'
        : `Sent to ${r.sent} open app${r.sent === 1 ? '' : 's'}.`);
    } catch (e) {
      setSaid(errorText(e));
    } finally {
      setBusy(false);
      void connected.refetch();
    }
  };

  return (
    <>
      <button className="btn sm ghost" disabled={busy} onClick={() => void push()}>
        {busy ? 'Pushing…' : `Push to open apps${online ? ` (${online})` : ''}`}
      </button>
      {said && <span className="li-sub" style={{ alignSelf: 'center' }}>{said}</span>}
    </>
  );
}

/** "1.0.0 (build 11)". The name has never changed; the build always does. */
function verLabel(r: { latest: string; build: number }): string {
  return `${r.latest} (build ${r.build})`;
}

/** The QR a tester scans to install. Encodes exactly the URL the app's own
 *  update check is given, so the two can never point at different builds. */
function InstallQr({ url, configHint }: { url: string | null; configHint: boolean }) {
  if (!url) {
    return (
      <div className="note" style={{ marginTop: 10 }}>
        <b>No build to download yet.</b>{' '}
        {/* This used to send operators to "the Config screen" to set
            app.android_url. There is no such screen and no such field — the
            row can only be written in the database — so the console now names
            only what it can actually do. */}
        {configHint
          ? <>Upload an APK below — that is the only way to set the download link from the console.</>
          : <>Upload an APK below.</>}
        {' '}Until then there is nothing to scan and the app's update prompt has nowhere to send anyone.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{ background: '#fff', padding: 10, borderRadius: 10, lineHeight: 0 }}
           dangerouslySetInnerHTML={{ __html: qrSvg(url, 168) }} />
      <div style={{ minWidth: 0, flex: '1 1 240px' }}>
        <div className="li-title" style={{ fontSize: 13.5 }}>Scan to install</div>
        <div className="li-sub" style={{ marginTop: 4 }}>
          Android will ask the tester to allow installs from the browser the first time. This is a
          sideload, not the Play Store.
        </div>
        <div className="li-sub mono" style={{ marginTop: 8, wordBreak: 'break-all' }}>{url}</div>
      </div>
    </div>
  );
}

/**
 * Upload an Android build.
 *
 * The version and build are typed, not read out of the APK: the browser cannot
 * parse the binary manifest, and they are the two numbers in pubspec.yaml
 * (`version: 1.2.0+7` is version 1.2.0, build 7). The server refuses a build
 * number that is not newer than the one it has.
 */
function UploadBuild({ app, current }: { app: 'driver' | 'agent'; current: AppRelease }) {
  const upload = useBuildUpload();
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState(current.latest);
  const [build, setBuild] = useState(String(current.build + 1));
  const [notes, setNotes] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const bad =
    !file ? 'Choose the APK.'
      : !file.name.toLowerCase().endsWith('.apk') ? 'That is not an .apk file.'
        : !/^\d+\.\d+\.\d+$/.test(version.trim()) ? 'Version looks like 1.2.0.'
          : !(Number.isInteger(Number(build)) && Number(build) > 0) ? 'Build is a whole number.'
            : null;

  const submit = () => {
    if (!file || bad) return;
    setMsg(null);
    upload.mutate(
      { app, file, version: version.trim(), build: Number(build), notes: notes.trim() || undefined },
      {
        onSuccess: (r) => {
          setMsg(`Uploaded ${r.latest} (build ${r.build}). Scan the QR to test it, then Publish to offer it to phones.`);
          setFile(null);
        },
        onError: (e: Error) => setMsg(errorText(e)),
      },
    );
  };

  return (
    <details style={{ marginTop: 12 }}>
      <summary className="li-title" style={{ cursor: 'pointer', fontSize: 13.5 }}>Upload a new Android build</summary>
      <div style={{ display: 'grid', gap: 8, marginTop: 10, maxWidth: 460 }}>
        <input type="file" accept=".apk,application/vnd.android.package-archive"
               onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="field mono" value={version} onChange={(e) => setVersion(e.target.value)}
                 aria-label="Version" placeholder="1.2.0" style={{ flex: 1 }} />
          <input className="field mono" value={build} onChange={(e) => setBuild(e.target.value)}
                 aria-label="Build number" inputMode="numeric" placeholder="build" style={{ width: 110 }} />
        </div>
        <input className="field" value={notes} onChange={(e) => setNotes(e.target.value)}
               placeholder="What changed (shown on the update screen)" aria-label="Release notes" />
        {file && <div className="li-sub mono">{file.name} · {(file.size / 1048576).toFixed(1)} MB</div>}
        <button className="btn sm" disabled={!!bad || upload.isPending} onClick={submit}>
          {upload.isPending ? 'Uploading…' : 'Upload build'}
        </button>
        {bad && file && <div className="li-sub" style={{ color: 'var(--bad)' }}>{bad}</div>}
        {msg && <div className="li-sub">{msg}</div>}
      </div>
    </details>
  );
}

const APP_LABEL: Record<'driver' | 'agent', { title: string; who: string }> = {
  driver: { title: 'Spark EV — driver app', who: 'driver' },
  agent: { title: 'Spark Agent — cash counter app', who: 'agent' },
};

export function Releases() {
  const { data, isLoading, error } = useReleases();
  const update = useReleaseMutations();
  const { data: cfg } = useConfig();
  const [failed, setFailed] = useState<string | null>(null);

  if (isLoading) return <Loading />;
  if (error || !data) return <Empty title="Release data is not available." hint={loadFailedHint(error)} />;
  if (!data.length) {
    return <Empty title="No app releases recorded." hint="A release row is created when a build is registered." />;
  }

  const configUrl = cfg?.find((c) => c.key === 'app.android_url')?.value?.trim() || null;
  const urlOf = (r: AppRelease) =>
    r.downloadUrl?.trim() || (r.app === 'driver' ? configUrl : null);

  const mutate = (v: Parameters<typeof update.mutate>[0]) => {
    setFailed(null);
    update.mutate(v, {
      onError: (e: Error) => setFailed(errorText(e)),
    });
  };

  const apps = (['driver', 'agent'] as const).filter((a) => data.some((r) => r.app === a));

  return (
    <>
      {failed && <div className="note" style={{ marginTop: 12, color: 'var(--bad)' }}>{failed}</div>}

      {apps.map((appName) => {
        const rows = data.filter((r) => r.app === appName);
        const android = rows.find((r) => r.platform === 'android');
        return (
          <section key={appName} style={{ marginTop: 18 }}>
            <div className="li-title" style={{ fontSize: 15, marginBottom: 8 }}>{APP_LABEL[appName].title}</div>
            <div className="tiles">
              {/* Version AND build, everywhere: the build is what moves and what
                  the update check compares. */}
              <Tile k="Android" v={android ? verLabel(android) : '—'}
                    d={android ? android.artifactName ?? 'no file' : 'no release row'} />
              <Tile k="Minimum supported" v={android?.minSupported ?? '—'}
                    d={android?.forceUpdate ? 'update forced' : 'update optional'} />
              <Tile k="Blocked below" v={android?.forceUpdate ? `build ${android.build}` : '—'}
                    d={android?.forceUpdate ? 'cannot open the app' : 'nobody blocked'} />
              <Tile k="Published" v={android?.published ? 'Yes' : 'No'}
                    d={android?.published ? `offered to ${APP_LABEL[appName].who}s` : 'not offered yet'} />
            </div>

            {rows.map((r) => (
              <div className="card" style={{ marginTop: 14 }} key={`${r.app}-${r.platform}`}>
                <div className="row">
                  <div>
                    <div className="li-title" style={{ fontSize: 14 }}>
                      {r.displayName} · {r.platform === 'android' ? 'Android' : 'iOS'}
                    </div>
                    <div className="li-sub mono">{r.packageName}</div>
                  </div>
                  <Pill tone={!r.published ? 'off' : r.forceUpdate ? 'mid' : 'on'}>
                    {!r.published ? 'Not published' : r.forceUpdate ? 'Force update on' : 'Update optional'}
                  </Pill>
                </div>

                {r.platform === 'android' && <InstallQr url={urlOf(r)} configHint={r.app === 'driver'} />}
                {r.platform === 'ios' && (
                  <div className="li-sub" style={{ marginTop: 10 }}>
                    iOS builds go through TestFlight or the App Store; uploads here are Android only.
                  </div>
                )}

                {r.artifactName && (
                  <div className="note" style={{ marginTop: 10 }}>
                    <span className="mono">{r.artifactName}</span>
                    <div className="li-sub">
                      {r.artifactSize}
                      {r.artifactSha256 && <> · sha256 {r.artifactSha256.slice(0, 16)}…</>}
                    </div>
                  </div>
                )}

                {r.notes && <div className="li-sub" style={{ margin: '10px 0' }}>{r.notes}</div>}

                {r.forceUpdate && (
                  <div className="note" style={{ marginBottom: 10 }}>
                    <b>Forced.</b> Anyone below build {r.build} is held at a blocking screen on next launch.
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button
                    className={`btn sm${r.published ? ' ghost' : ''}`}
                    disabled={update.isPending}
                    onClick={() => mutate({ app: r.app, platform: r.platform, published: !r.published })}
                  >
                    {r.published ? 'Unpublish' : `Publish ${r.latest} (${r.build})`}
                  </button>
                  {/* The live push is the driver app's socket. Spark Agent checks
                      on launch, on resume and every few minutes. */}
                  {r.published && r.app === 'driver' && <PushButton platform={r.platform} />}
                  {r.published && (
                    <button
                      className={`btn sm${r.forceUpdate ? ' ghost' : ''}`}
                      disabled={update.isPending}
                      onClick={() => mutate({
                        app: r.app,
                        platform: r.platform,
                        forceUpdate: !r.forceUpdate,
                        ...(r.forceUpdate ? {} : { minSupported: r.latest }),
                      })}
                    >
                      {r.forceUpdate ? 'Lift forced update' : `Force update to ${verLabel(r)}`}
                    </button>
                  )}
                </div>

                {r.platform === 'android' && <UploadBuild app={r.app} current={r} />}

                <div className="li-sub" style={{ marginTop: 10 }}>
                  {r.updatedBy
                    ? <>Last changed by {r.updatedBy} · {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—'}</>
                    : <>No changes made from the console yet.</>}
                </div>
              </div>
            ))}
          </section>
        );
      })}
    </>
  );
}
